/**
 * @file Fixed bottom-right "Drop or browse files" affordance on the Home,
 * Forms-list, and Form pages. Clicking opens the file picker; a selected file is
 * handed to `onFileBrowse`. For an intake reviewer (`pdfsOpenInIntake`) a PDF
 * opens its PDF intake schedule review (S2); any other file starts an agent
 * import run (the agent panel opens on its own and the Mapping Review panel
 * surfaces progress and approval). The chip's tooltip says which.
 * When `onDrop` is supplied (the Form page), dragging a file over the page
 * reveals a full-width strip along the bottom 50px of the viewport; dropping on
 * it hands the file to `onDrop` for AI import into the open form. The strip sits
 * below the form's own attachment/comms drop boxes (which live in the scrollable
 * content above it), so it never blocks them. A small keyboard-shortcuts launcher
 * sits immediately to the left of the chip.
 *
 * `importDisabled` disables the import affordances rather than removing them:
 * the chip stays mounted and inert (`aria-disabled`, out of the tab order) and
 * only the drag strip — a transient overlay, not a control — stays away. The
 * fixed container and its keyboard-shortcuts launcher always render, because the
 * launcher is the sole discovery point for the whole shortcut schema and must not
 * be collateral damage of the unrelated `aiAgent` permission gate; pages
 * therefore render DropHint unconditionally. Keeping the chip mounted also pins
 * the launcher's screen position: the container is anchored by `right` with no
 * width, so unmounting the chip beside it would slide the launcher across the
 * viewport the moment the permission or the form version resolved. At antd `md`
 * and wider the container sits beside the Agent launcher; below `md` there is no
 * room beside it, so the container stacks above the launcher instead.
 */
import { type FC, useRef, useState, useEffect, useCallback } from 'react';
import { Grid, Tooltip, Typography, theme } from 'antd';
import { CloudUploadOutlined } from '@ant-design/icons';
import { KeyboardShortcutHint } from '@/keyboard';

const { Text } = Typography;

/** Width of the Agent launcher, the bottom-right button the chip sits beside at `md` and wider. */
const AGENT_LAUNCHER_CLEARANCE = 120;

interface DropHintProps {
  onFileBrowse: (file: File) => void;
  onDrop?: (file: File) => void;
  validExtensions?: string[];
  importDisabled?: boolean;
  /** The caller sends PDFs to PDF intake rather than the agent; only the tooltip reads it. */
  pdfsOpenInIntake?: boolean;
}

const DropHint: FC<DropHintProps> = ({
  onFileBrowse,
  onDrop,
  validExtensions = [],
  importDisabled = false,
  pdfsOpenInIntake = false,
}) => {
  const { token } = theme.useToken();
  const { md } = Grid.useBreakpoint();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [hovered, setHovered] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [pageDragging, setPageDragging] = useState(false);
  const dragDepth = useRef(0);
  const canDrop = Boolean(onDrop) && !importDisabled;

  const handleClick = useCallback(() => {
    if (importDisabled) return;
    fileInputRef.current?.click();
  }, [importDisabled]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        onFileBrowse(file);
      }
      e.target.value = '';
    },
    [onFileBrowse],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (!onDrop) return;
      e.preventDefault();
    },
    [onDrop],
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      if (!onDrop) return;
      e.preventDefault();
      setDragActive(true);
    },
    [onDrop],
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      if (!onDrop) return;
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
      setDragActive(false);
    },
    [onDrop],
  );

  const handleFileDrop = useCallback(
    (e: React.DragEvent) => {
      if (!onDrop) return;
      e.preventDefault();
      setDragActive(false);
      const file = e.dataTransfer.files[0];
      if (file) onDrop(file);
    },
    [onDrop],
  );

  useEffect(() => {
    if (!canDrop) return;
    const carriesFiles = (e: DragEvent) =>
      Array.from(e.dataTransfer?.types ?? []).includes('Files');
    const onEnter = (e: DragEvent) => {
      if (!carriesFiles(e)) return;
      dragDepth.current += 1;
      setPageDragging(true);
    };
    const onLeave = () => {
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setPageDragging(false);
    };
    const onEnd = () => {
      dragDepth.current = 0;
      setPageDragging(false);
    };
    window.addEventListener('dragenter', onEnter);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('drop', onEnd);
    window.addEventListener('dragend', onEnd);
    return () => {
      window.removeEventListener('dragenter', onEnter);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('drop', onEnd);
      window.removeEventListener('dragend', onEnd);
    };
  }, [canDrop]);

  const expanded = canDrop && pageDragging;

  return (
    <>
      <div
        style={{
          position: 'fixed',
          ...(md
            ? { bottom: token.marginSM, right: token.marginSM + AGENT_LAUNCHER_CLEARANCE }
            : { bottom: token.marginSM * 2 + token.controlHeightSM, right: token.marginSM }),
          zIndex: 100,
          display: 'flex',
          alignItems: 'center',
          gap: token.paddingXS,
        }}
      >
        <KeyboardShortcutHint />
        <Tooltip
          title={
            pdfsOpenInIntake
              ? 'PDFs open in Intake; other files open in the agent'
              : 'Files open in the agent'
          }
        >
          <div
            role="button"
            tabIndex={importDisabled ? -1 : 0}
            aria-disabled={importDisabled}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: token.paddingXS,
              paddingInline: token.padding,
              borderRadius: 20,
              cursor: importDisabled ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
              filter:
                hovered && !importDisabled ? 'brightness(0.95)' : undefined,
              outline: 'none',
              backgroundColor: token.colorFillQuaternary,
            }}
            aria-label="Browse files for import"
          >
            <CloudUploadOutlined
              style={{
                color: importDisabled
                  ? token.colorTextDisabled
                  : token.colorTextSecondary,
              }}
            />
            <Text type="secondary" disabled={importDisabled}>
              Drop or browse files
            </Text>
          </div>
        </Tooltip>
      </div>
      {expanded && (
        <div
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleFileDrop}
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            height: 80,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'flex-start',
            padding: token.paddingSM,
            justifyContent: 'center',
            gap: token.paddingSM,
            borderTop: `2px dashed ${token.colorPrimary}`,
            backgroundColor: dragActive
              ? token.colorPrimaryBg
              : token.colorBgElevated,
            boxShadow: token.boxShadowSecondary,
            transition: 'background-color 0.2s ease',
          }}
          aria-label="Drop file to extract with AI"
        >
          <CloudUploadOutlined
            style={{ color: token.colorPrimary, fontSize: 20 }}
          />
          <Text strong>Drop file to extract with AI</Text>
          {validExtensions.length > 0 && (
            <Text type="secondary">{validExtensions.join(', ')}</Text>
          )}
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={validExtensions.join(',')}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        disabled={importDisabled}
        aria-hidden
      />
    </>
  );
};

export default DropHint;
